/**
 * useIncidents - Shared React Query hook for fetching the incidents list
 *
 * Replaces the pattern of fetching incidents via api.get() and storing
 * them in the Zustand incident store. Uses useTenantQuery for automatic
 * auth/tenant injection and caching.
 *
 * Query key is compatible with getIncidentsQueryKey() from
 * useOptimisticIncidents.ts, so mutations automatically invalidate this query.
 */

import { useTenantQuery } from './useTenantQuery';
import { Incident } from '@/types/incident';

interface UseIncidentsOptions {
  status?: string;
  limit?: number;
  enabled?: boolean;
}

/**
 * Fetch a list of incidents with optional filters.
 *
 * @example
 * const { data: incidents = [], isLoading } = useIncidents({
 *   status: 'active,stabilizing,planned',
 *   limit: 50,
 * });
 */
export function useIncidents(options: UseIncidentsOptions = {}) {
  const { status, limit, enabled } = options;

  const params: Record<string, any> = {};
  if (status) params.status = status;
  if (limit) params.limit = limit;

  const filters = Object.keys(params).length > 0 ? params : undefined;

  return useTenantQuery<{ data: Incident[] }, Incident[]>(
    filters ? ['incidents', filters] : ['incidents'],
    '/incidents',
    {
      params: filters,
      enabled,
      queryOptions: { select: (resp) => resp.data },
    }
  );
}

/**
 * Convenience hook for active/stabilizing/planned incidents.
 * Used by IncidentSwitcher, Settings pages, and anywhere the
 * "current working set" of incidents is needed.
 */
export function useActiveIncidents() {
  return useIncidents({ status: 'active,stabilizing,recovery,planned', limit: 50 });
}
